import './Select.css';
import React, { useState, useRef, ReactNode } from 'react';
import classnames from 'classnames';
import { Icon } from '../../Icon/Icon';
import { IconName } from '../../Icon/IconName';
import { t } from '../../../locales/translate';
import { Spinner } from '../../Spinner/Spinner';
import { FormField } from '../../../model/form';
import { presence } from '../../../util/util';
import { Collapse } from '@nerdgeschoss/shimmer-component-collapse';
import { useId } from '../../../util/react-util';

export interface Option<T = string> {
  value: T;
  label?: string;
  disabled?: boolean;
}

export interface Props<T> extends FormField<T | null> {
  label?: string;
  options?: readonly Option<T>[];
  icon?: IconName;
  actions?: ReactNode;
  loading?: boolean;
  loadingMsg?: string;
  displayWithoutOptions?: boolean;
  compact?: boolean;
  widthAuto?: boolean;
  allowEmptyMessage?: string;
  fullBorder?: boolean;
}

export function FormSelect<T = string>({
  name,
  inputId,
  label,
  value,
  required,
  disabled,
  readOnly,
  options,
  icon,
  actions,
  description,
  className,
  style,
  loading,
  loadingMsg,
  displayWithoutOptions,
  placeholder,
  allowEmpty,
  allowEmptyMessage = '',
  errors,
  nullIfEmpty,
  onChange,
  touched,
  valid,
  compact,
  widthAuto,
  fullBorder,
}: Props<T>): JSX.Element {
  const [active, setActive] = useState(false);
  const displayErrors = touched && !valid;
  const full = active || displayWithoutOptions || !!placeholder || !!value;
  const id = useId(inputId);

  const inputRef: React.MutableRefObject<HTMLSelectElement | null> = useRef(null);

  if (loading) {
    return <Spinner color="red" text={loadingMsg || t.actions.loading('')} />;
  } else if (!displayWithoutOptions && (!options || options.length === 0)) {
    return <></>;
  }

  return (
    <div
      className={classnames(
        'select',
        'input',
        {
          'input--with-icon': icon,
          'select--compact': !!compact,
          'select--width-auto': widthAuto,
          'select--full-border': fullBorder,
        },
        className
      )}
      style={style}
      onClick={() => {
        inputRef.current?.focus();
        inputRef.current?.showPicker();
      }}
    >
      {icon && <Icon name={icon} className="input__icon" />}
      <div
        className={classnames('input__container', {
          'input__container--active': active,
          'input__container--full': full,
          'input__container--error': displayErrors,
        })}
      >
        <div className="input__input-wrapper">
          {!!label && (
            <label className="input__label" htmlFor={id}>
              {label}
              {!required && ` (${t.general.optional})`}
            </label>
          )}
          <div className="select__select-wrapper">
            <select
              id={id}
              name={name}
              className="input__input"
              onChange={(v) => {
                const newValue = nullIfEmpty ? (presence(v.target.value) ?? null) : v.target.value;
                onChange?.(newValue as T);
                v.target.blur();
              }}
              value={(value as string) ?? ''}
              onFocus={() => {
                setActive(true);
              }}
              onBlur={() => {
                setActive(false);
              }}
              required={required}
              disabled={disabled || readOnly}
              ref={inputRef}
            >
              {allowEmpty && <option value="">{allowEmptyMessage}</option>}
              {options &&
                options.map((option) => {
                  return (
                    <option key={option.value as string} value={option.value as string} disabled={option.disabled}>
                      {option.label ? option.label : option.value}
                    </option>
                  );
                })}
            </select>

            <Icon className="select__arrow-down" name="arrow-triangle-down--sm" />
          </div>
          {actions && <div className="input__actions">{actions}</div>}
          {displayErrors && <Icon name="alert-circle--sm" className="input__alert-circle" />}
        </div>
        <div className="input__description-wrapper">
          {description && <div className="input__description">{description}</div>}
          <Collapse open={displayErrors}>
            {errors?.map((error) => {
              return (
                <div className="input__error-message" key={error}>
                  {t.errors[error]}
                </div>
              );
            })}
          </Collapse>
        </div>
      </div>
    </div>
  );
}
